// components/logistics/JobClaimModal.jsx
import React, { useState } from 'react';
import Modal from '../common/Modal';
import { Calendar, Clock, MapPin, Award, AlertCircle, Check } from 'lucide-react';
import SkillBadge from '../trust/SkillBadge';

const JobClaimModal = ({ isOpen, onClose, job, matchResult, onConfirm }) => {
  const [isAgreed, setIsAgreed] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);

  if (!job) return null;

  const handleClaim = async () => {
    if (!isAgreed) return;
    
    setIsClaiming(true);
    try {
      await onConfirm(job);
      onClose();
    } catch (error) {
      console.error('Error claiming job:', error);
      setIsClaiming(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Claim Job"
      size="medium"
    >
      <div className="space-y-4">
        {/* Job Header */}
        <div className="border-b pb-4">
          <h3 className="text-xl font-semibold mb-1">{job.title}</h3>
          <p className="text-gray-600">{job.groupTitle}</p>
          <div className="mt-2 flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Award className="text-yellow-500" size={20} />
              <span className="font-bold text-lg">{job.tokenReward} tokens</span>
            </div>
            <div className="text-sm text-gray-600">
              ~{job.estimatedHours} hours
            </div>
          </div>
        </div>

        {/* Job Details */}
        <div className="space-y-3">
          <div>
            <h4 className="font-medium mb-1">Description</h4>
            <p className="text-sm text-gray-700">{job.description}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-1 flex items-center gap-1">
                <Calendar size={16} />
                Schedule
              </h4>
              <p className="text-sm text-gray-700">
                {new Date(job.schedule?.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p className="text-sm text-gray-600">
                {job.schedule?.startTime} - {job.schedule?.endTime}
                {job.schedule?.flexible && (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                    Flexible timing
                  </span>
                )}
              </p>
            </div>

            <div>
              <h4 className="font-medium mb-1 flex items-center gap-1">
                <MapPin size={16} />
                Location
              </h4>
              <p className="text-sm text-gray-700">{job.location}</p>
            </div>
          </div>
        </div>

        {/* Skills Verification */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-medium mb-2 flex items-center gap-2 text-green-800">
            <Check size={18} />
            You're Qualified!
          </h4>
          <div className="space-y-2">
            <div>
              <p className="text-xs text-gray-600 mb-1">Your Skills:</p>
              <div className="flex flex-wrap gap-1">
                {matchResult.matchingSkills.map(skill => (
                  <SkillBadge key={skill.id} skill={skill} size="small" />
                ))}
              </div>
            </div>
            <div className="text-sm text-green-700">
              Trust Score: {matchResult.meetsTrustRequirement ? '✓ Meets requirement' : '✗ Below requirement'}
            </div>
          </div>
        </div>

        {/* Terms Agreement */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isAgreed}
              onChange={(e) => setIsAgreed(e.target.checked)}
              className="mt-1"
            />
            <div className="text-sm text-gray-700">
              I understand that by claiming this job, I commit to:
              <ul className="mt-2 space-y-1 ml-4">
                <li>• Completing the work as described</li>
                <li>• Being available during the scheduled time</li>
                <li>• Maintaining professional standards</li>
                <li>• Communicating any issues promptly</li>
              </ul>
            </div>
          </label>
        </div>

        {/* Warning if close to deadline */}
        {job.hoursUntilDeadline < 24 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="text-yellow-600 mt-0.5" size={16} />
            <p className="text-sm text-yellow-800">
              <strong>Urgent:</strong> This job starts in less than 24 hours. 
              Make sure you can commit to the scheduled time.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            disabled={isClaiming}
          >
            Cancel
          </button>
          <button
            onClick={handleClaim}
            disabled={!isAgreed || isClaiming}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
              isAgreed && !isClaiming
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isClaiming ? 'Claiming...' : 'Confirm & Claim Job'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default JobClaimModal;